import { create } from "zustand";
import { toast } from "sonner";
import { Campaign, Creator, OrgCreatorsFacets } from "@/types/types";
import { CreatorsTableQuery } from "@/components/creators-table/types";
import { useCreatorsStore } from "./creatorsStore";

/**
 * The Global CRM page's server pager. One call = one page of the org's
 * campaign creators (POST /api/campaigns/getOrgCreators) for the table's
 * current query; the rows land in useCreatorsStore so every cell, dialog and
 * the thread panel keep reading creators from the one place they always did.
 * Facets ride along only on a "full" fetch (first load, refresh after a
 * mutation) and the campaigns only on the very first one: their identity
 * feeds the table's column definitions, so re-sending them on every refresh
 * would rebuild every column and remount every cell.
 */
interface GlobalCrmState {
  total: number;
  campaigns: Campaign[];
  facets: OrgCreatorsFacets | null;
  loading: boolean;
  /** The first response landed (campaigns + facets are known). */
  loaded: boolean;
  error: string | null;
  /** The query the rows on screen answer. */
  query: CreatorsTableQuery | null;
  fetchPage: (
    query: CreatorsTableQuery,
    organizationId: string,
    token: string,
    opts?: { full?: boolean }
  ) => Promise<void>;
  reset: () => void;
}

interface OrgCreatorsResponse {
  success: boolean;
  error?: string;
  data?: Creator[];
  total?: number;
  campaigns?: Campaign[];
  facets?: OrgCreatorsFacets | null;
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

// Responses can land out of order (a slow page 3 after a quick page 4);
// only the newest request may write.
let requestSeq = 0;

export const useGlobalCrmStore = create<GlobalCrmState>((set, get) => ({
  total: 0,
  campaigns: [],
  facets: null,
  loading: false,
  loaded: false,
  error: null,
  query: null,

  fetchPage: async (query, organizationId, token, opts) => {
    const seq = ++requestSeq;
    const full = opts?.full ?? !get().loaded;
    set({ loading: true, error: null });
    try {
      const response = await fetch(`${API_BASE_URL}/api/campaigns/getOrgCreators`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          organization_id: organizationId,
          page_index: query.pageIndex,
          page_size: query.pageSize,
          sorting: query.sorting,
          global_filter: query.globalFilter,
          column_filters: query.columnFilters,
          include_facets: full,
          include_campaigns: !get().loaded,
        }),
      });
      const payload = (await response.json()) as OrgCreatorsResponse;
      if (seq !== requestSeq) return; // superseded
      if (!response.ok || !payload.success) {
        throw new Error(payload.error || "Failed to load creators");
      }

      const campaigns = payload.campaigns ?? get().campaigns;
      const facets = payload.facets !== undefined ? payload.facets : get().facets;
      useCreatorsStore.getState().setCreators(payload.data ?? []);
      set({
        total: payload.total ?? 0,
        campaigns,
        facets,
        query,
        loading: false,
        loaded: true,
      });
    } catch (error) {
      if (seq !== requestSeq) return;
      set({ loading: false, error: (error as Error).message });
      toast.error("Couldn't load creators", { description: (error as Error).message });
    }
  },

  reset: () => {
    requestSeq++;
    set({
      total: 0,
      campaigns: [],
      facets: null,
      loading: false,
      loaded: false,
      error: null,
      query: null,
    });
  },
}));
